/* eslint-disable max-lines */
import _, { camelCase, capitalize } from 'lodash';
import React from 'react';
import Text from 'src/@vymo/ui/atoms/text';
import { AlertProps } from 'src/@vymo/ui/blocks/alert/types';
import {
  FormFieldState,
  InputFieldConfig,
} from 'src/@vymo/ui/molecules/form/types';
import { Keys, locale } from 'src/i18n';
import { getRevyreModuleLabelByCode } from 'src/models/lmsConfig/queries';
import {
  BULK_UPLOAD,
  SUPERVISOR_REVIEW_DOMAINTYPE,
} from 'src/modules/constants';
import { DISPOSITION_GROUP } from '../constants';
import { BULK_REVOKE } from '../ddp/constants';
import { DomainType, GroupConfig, ScenarioType, WorkflowType } from '../types';
import { RevyreFormProps, SubmitPayload, UploadActionType } from './types';
import styles from './index.module.scss';

const excludedUIFieldCodes = ['selectState'];

const getDocumentServiceType = (
  workflowType?: WorkflowType,
  domainType?: DomainType,
) => (domainType === 'workflow' ? workflowType : domainType || workflowType);

export const getDocumentUploadUrl = ({
  baseUrl,
  workflowType,
  domainType,
}: {
  baseUrl?: string;
  workflowType?: WorkflowType;
  domainType?: DomainType;
}) => {
  const serviceType = getDocumentServiceType(workflowType, domainType);

  if (!baseUrl) {
    return undefined;
  }

  return serviceType ? `${baseUrl}/${serviceType}/document` : `${baseUrl}/dms`;
};

export const getDocumentDownloadUrl = ({
  baseUrl,
  workflowType,
  domainType,
  type,
  entityId,
}: {
  baseUrl?: string;
  workflowType?: WorkflowType;
  domainType?: DomainType;
  type?: ScenarioType;
  entityId?: string;
}) => {
  const serviceType = getDocumentServiceType(workflowType, domainType);

  return baseUrl && serviceType && type === 'update' && entityId
    ? `${baseUrl}/${serviceType}/${entityId}/document`
    : undefined;
};

// eslint-disable-next-line complexity
export const getFormTitle = (
  type: ScenarioType,
  workflowType: WorkflowType,
  domainType: DomainType,
  entityName: string,
) => {
  switch (domainType) {
    case 'activity':
      if (type === 'update') {
        return `${locale(Keys.UPDATE)} ${entityName ?? ''}`;
      }
      if (type === 'detailsCreate') {
        return `${locale(Keys.CREATE)} ${locale(Keys.ACTIVITY)}`;
      }
      return `${locale(Keys.CREATE_ACTIVITY)}`;

    case 'payment':
      if (type === 'update') {
        return `${locale(Keys.UPDATE)} ${entityName ?? ''} ${locale(
          Keys.PAYMENT,
        )}`;
      }
      return `${locale(Keys.CREATE_PAYMENT)}`;

    case SUPERVISOR_REVIEW_DOMAINTYPE:
      if (type === 'update') {
        return `${locale(Keys.UPDATE)} ${locale(Keys.SUPERVISOR_REVIEW)}`;
      }
      return `${locale(Keys.CREATE)} ${locale(Keys.SUPERVISOR_REVIEW)}`;

    default: {
      const fallbackTitle = getRevyreModuleLabelByCode(workflowType);

      if (type === 'update') {
        return `${locale(Keys.UPDATE)} ${fallbackTitle}`;
      }
      return `${locale(Keys.CREATE)} ${fallbackTitle}`;
    }
  }
};

export const isWorkflowIdWithValue = (code, entityId) =>
  code === 'workflowId' && entityId;

export const getUIFieldsGrouping = (fields: InputFieldConfig[]) => [
  {
    code: 'uiFields',
    name: locale(Keys.UI_FIELDS),
    fields: fields.map((field) => field.code),
  },
];

export const getActivityUIFieldsGrouping = ({
  initialUiFields,
  onActivitySelection,
}: {
  initialUiFields?: InputFieldConfig[];
  onActivitySelection?: InputFieldConfig[];
}) => [
  {
    code: 'engagement',
    name: locale(Keys.ENGAGEMENT),
    fields: (initialUiFields || []).map((field) => field.code),
  },
  {
    code: 'engagementDetails',
    name: locale(Keys.ENGAGEMENT_DETAILS),
    fields: (onActivitySelection || [])
      .filter((field) => field.code !== DISPOSITION_GROUP)
      .map((field) => field.code),
  },
  {
    code: 'engagementOutcome',
    name: locale(Keys.ENGAGEMENT_OUTCOME),
    fields: (onActivitySelection || [])
      .filter((field) => field.code === DISPOSITION_GROUP)
      .map((field) => field.code),
  },
];

export const mapInputsToFormValues = (inputsMap) => {
  const valuesMap = {};

  if (!_.isEmpty(inputsMap)) {
    Object.keys(inputsMap).forEach((key) => {
      valuesMap[key] = {
        value: inputsMap[key],
      };
    });
  }

  return valuesMap;
};

export const getWorkflowConfigWithHierarchy = (
  workflowConfig: Record<string, any> = {},
  users: Record<string, any> = {},
  hierarchy: Record<string, any> = {},
) => {
  const mergedWorkflowConfig = {
    ...workflowConfig,
  };

  const actorColumnConfigs = Array.isArray(users?.columnConfig)
    ? users.columnConfig
    : [];

  if (actorColumnConfigs.length) {
    mergedWorkflowConfig.actor = {
      ...(workflowConfig.actor || {}),
      columnConfigs: actorColumnConfigs,
      hierarchy: true,
    };
  }

  Object.entries(hierarchy?.columnConfig || {}).forEach(
    ([hierarchyType, columnConfigs]) => {
      const hierarchyColumnConfigs = Array.isArray(columnConfigs)
        ? columnConfigs
        : [];

      if (!hierarchyColumnConfigs.length) {
        return;
      }

      mergedWorkflowConfig[hierarchyType] = {
        ...(workflowConfig[hierarchyType] || {}),
        columnConfigs: hierarchyColumnConfigs,
        hierarchy: true,
      };
    },
  );

  return mergedWorkflowConfig;
};

export const applyEntitySelectionRules = (
  inputs: InputFieldConfig[] = [],
  groups: GroupConfig[] = [],
  inputsMap: Record<string, any> = {},
  entityId?: string,
  type?: string,
  domainType?: DomainType,
) => {
  const updatedInputsMap: any = {
    ...inputsMap,
    workflowId: inputsMap.workflowId || entityId,
  };
  const processedInputs = inputs.map((inp) => {
    let { disabled } = inp;

    if (inp.type === 'entity_selection') {
      const workflowIdValue = isWorkflowIdWithValue(
        inp.code,
        updatedInputsMap?.id,
      );
      const shouldDisableEntitySelection =
        updatedInputsMap[inp.code] != null &&
        !(type === 'create' && domainType === 'activity');

      disabled = shouldDisableEntitySelection
        ? Boolean(updatedInputsMap[inp.code] || workflowIdValue)
        : inp.disabled;
    }

    return {
      ...inp,
      disabled,
    };
  });

  return { inputs: processedInputs || [], groups };
};

export const getInputsAndGroups = (events, eventId) => {
  const currentEventConfig = _.find(events, { allowedAction: eventId });
  const { inputs = [], groups = [] } = currentEventConfig?.config || {};

  return {
    inputs,
    groups,
  };
};

const getNestedChildrenCodes = (
  inputField: InputFieldConfig,
  inputsMap: Record<string, any>,
) => {
  const fieldCodes: string[] = [];
  const selectedValue = inputsMap[inputField.code];
  if (selectedValue) {
    if (inputField?.sifg_options?.inputs?.[selectedValue]) {
      inputField.sifg_options.inputs[selectedValue].forEach((childField) => {
        fieldCodes.push(childField.code);

        if (childField.type === 'sifg') {
          fieldCodes.push(...getNestedChildrenCodes(childField, inputsMap));
        }

        if (childField.type === 'profile_selection') {
          fieldCodes.push(
            camelCase(`${childField.code}_${childField.sub_type}`),
          );
        }
      });
    }
  }

  return fieldCodes;
};

export const getActivityFormPayload = (
  result: {
    data: Array<{ code: string; value: any; type: string; data?: any }>;
  },
  standardInputs: Array<InputFieldConfig>,
  workflowType: string,
) => {
  const eventPayload: Record<string, any> = {};
  const activityPayload: Record<string, any> = {};
  let eventId: string = '';

  const inputsMap = result?.data.reduce((acc, inputField) => {
    acc[inputField.code] = inputField.value;
    return acc;
  }, {});

  const selectedActivityInp = result?.data?.find(
    (inp) => inp.code === 'activityType',
  );

  const selectedWorkflowType =
    workflowType || selectedActivityInp?.data?.id?.split?.('__')?.[0] || '';

  const standardFieldCodes = _.flattenDeep(
    standardInputs.map((field) => {
      if (field.type === 'sifg' || field.type === 'profile_selection') {
        const childrenCodes = getNestedChildrenCodes(field, inputsMap);
        return [field.code, ...childrenCodes];
      }

      return field.code;
    }),
  );

  _.forEach(result?.data, (input) => {
    const { code } = input;
    const value = input?.value;

    if (input?.type === 'entity_next_event') {
      eventId = value;
    }

    if (standardFieldCodes.includes(code)) {
      activityPayload[code] = value;
    } else {
      eventPayload[code] = value;
    }
  });

  return {
    activityPayload: {
      ...activityPayload,
      workflowType: selectedWorkflowType,
    },
    ...(!_.isEmpty(eventPayload) && { eventPayload }),
    ...(!_.isEmpty(eventId) && { eventId }),
  };
};

// eslint-disable-next-line complexity
export const getBodyPayloadForSubmit = (
  type: ScenarioType,
  result: { data: Array<{ code: string; value: any; type: string }> },
  standardInputs: Array<InputFieldConfig>,
  domainType: DomainType,
  workflowType: WorkflowType,
) => {
  let payload: SubmitPayload = {};
  if (domainType === 'activity') {
    switch (type) {
      case 'create':
      case 'createGlobal':
      case 'detailsCreate':
      case 'update':
        payload = getActivityFormPayload(result, standardInputs, workflowType);
        break;

      default:
        return {};
    }
  } else if (domainType === SUPERVISOR_REVIEW_DOMAINTYPE) {
    switch (type) {
      case 'create':
        _.forEach(result?.data, (input) => {
          payload[input?.code] = input?.value;
        });
        break;

      case 'update':
      case 'detailsCreate':
        _.forEach(result?.data, (input) => {
          payload[input?.code] = input?.value;
        });
        payload = { ...payload, workflowType };
        break;

      default:
        return {};
    }
  } else {
    switch (type) {
      case 'create':
        _.forEach(result?.data, (input) => {
          payload[input?.code] = input?.value;
        });
        break;

      case 'createGlobal':
        _.forEach(result?.data, (input) => {
          payload[input?.code] = input?.value;
        });
        break;

      case 'detailsCreate':
        _.forEach(result?.data, (input) => {
          if (!excludedUIFieldCodes.includes(input?.code)) {
            payload[input?.code] = input?.value;
          }
        });
        break;
      case 'update':
        _.forEach(result?.data, (input) => {
          if (!excludedUIFieldCodes.includes(input?.code)) {
            payload[input?.code] = input?.value;
          }
        });
        break;
      default:
        return {};
    }
  }

  return payload;
};

export const getShowPageHeader = (
  type: RevyreFormProps['type'],
  showTitle?: boolean,
) =>
  showTitle !== false && ['detailsCreate', 'create', 'update'].includes(type);

// Normalizes code-name spinner submissions to send the selected code when available.
export const getNormalizedInputValueForCodeNameSpinner = (
  input: Record<string, any>,
) => {
  if (input?.data?.code) return input.data.code;

  if (!input?.outputType?.json && _.isObject(input?.value)) {
    return input.value?.code;
  }

  return input?.value;
};

export const normalizeSubmissionData = (
  inputs: Array<Record<string, any>> = [],
) =>
  inputs.map((input) =>
    input?.type === 'code_name_spinner'
      ? {
          ...input,
          value: getNormalizedInputValueForCodeNameSpinner(input),
        }
      : input,
  );

export const getFormData = (
  inputsMap: Record<string, any>,
  formData: Record<string, { value; type }>,
  domainType: DomainType,
  workflowType: WorkflowType,
  activityType: string,
  entityId?: string,
) => {
  const updatedInputsMap = {
    ...inputsMap,
    workflowType: inputsMap.workflowType || workflowType,
    activityType: inputsMap.activityType || activityType,
    workflowId: inputsMap.workflowId || entityId,
  };
  Object.keys(updatedInputsMap)?.forEach((code) => {
    formData[code] = { ...formData[code], value: updatedInputsMap[code] };
  });

  return formData;
};

export const getUpdateFields = (allowedActions) => [
  {
    code: 'selectState',
    hint: 'Select State',
    placeholder: 'Select value',
    returnCode: true,
    required: true,
    type: 'code_name_spinner',
    code_name_spinner_options: allowedActions.map((action) => ({
      code: action?.allowedAction,
      name: action?.eventName ?? action?.allowedAction,
    })),
  },
];

const getSelectEntityHint = (workflowType: string) =>
  `Select ${capitalize(workflowType)}`;

export const getActivityFields = (
  type: ScenarioType,
  activityTypes,
  workflowType: WorkflowType,
  workflowTypesWithActivities,
) => {
  const inputs: InputFieldConfig[] = [];

  if (type === 'create') {
    inputs.push({
      code: 'activityType',
      hint: locale(Keys.ACTIVITY_TYPE),
      placeholder: 'Select value',
      required: true,
      returnCode: true,
      type: 'code_name_spinner',
      meta: {
        viewType: 'select',
      },
      codeNameSpinnerOptions: workflowTypesWithActivities,
    });
  }

  if (type === 'detailsCreate') {
    inputs.push({
      code: 'activityType',
      hint: 'Select Activity',
      placeholder: 'Select value',
      required: true,
      returnCode: true,
      type: 'code_name_spinner',
      codeNameSpinnerOptions: activityTypes,
    });
  }

  const isEntitySelectionOld =
    localStorage.getItem('isEntitySelectionOld') === 'true';

  const isEntitySelectionValueString =
    localStorage.getItem('isEntitySelectionValueString') === 'true';

  if (workflowType) {
    const workflowLabel = getRevyreModuleLabelByCode(workflowType);
    const workflowHintLabel = workflowLabel || '';
    if (isEntitySelectionOld) {
      inputs.push({
        code: 'workflowId',
        hint: getSelectEntityHint(workflowHintLabel),
        placeholder: 'Select value',
        required: false,
        type: 'referral',
        returnCode: true,
        online: false,
        source: workflowType,
      });
    } else {
      inputs.push({
        code: 'workflowId',
        hint: getSelectEntityHint(workflowHintLabel),
        placeholder: 'Select value',
        required: true,
        type: 'entity_selection',
        returnCode: isEntitySelectionValueString,
        online: false,
        workflowType,
        multi: false,
      });
    }
  }
  return inputs;
};

export const getWorkflowCode = (inputValue) => {
  let parsedValue;

  try {
    parsedValue = JSON.parse(inputValue);
  } catch {
    parsedValue = inputValue;
  }

  const workflowCode = Array.isArray(parsedValue)
    ? parsedValue?.[0]?.code ?? ''
    : parsedValue;

  return workflowCode;
};

export const getEntityIdForInputsMap = (
  type: ScenarioType,
  domainType: DomainType,
  workflowIdValue: string,
  entityId?: string,
) =>
  type === 'create' && domainType === 'activity'
    ? getWorkflowCode(workflowIdValue)
    : entityId;

export const getActivityUpdateDescription = ({
  domainType,
  type,
  activityType,
  inputsMap,
}) => {
  if (domainType !== 'activity' || type !== 'update') return undefined;

  return {
    type: 'info' as AlertProps['variant'],
    content: (
      <div className={styles.formDescription}>
        <Text>
          <Text semiBold>{locale(Keys.ACTIVITY_TYPE)}:</Text>&nbsp;
          {capitalize(activityType)}
        </Text>
        {inputsMap?.name && (
          <Text>
            <Text semiBold>{locale(Keys.NAME)}:</Text>&nbsp;
            {capitalize(inputsMap?.name)}
          </Text>
        )}
      </div>
    ),
  };
};

export const getActivityInputsByCompletion = (
  activityTypesForWorkflow,
  isActivityCompletedValue: boolean | undefined,
  activityType: string,
  formData?: Record<
    string,
    {
      additionalData?;
      value;
      type;
    }
  >,
) => {
  const workflowIdValue = formData?.workflowId?.value;

  if (!workflowIdValue) return [];

  const {
    create_inputs: activityCreateInputs = [],
    complete_inputs: activityCompleteInputs = [],
  } =
    activityTypesForWorkflow?.find(
      (activity) =>
        activity.code === activityType || activity.name === activityType,
    ) || {};

  if (isActivityCompletedValue === true) {
    return activityCompleteInputs;
  }

  if (isActivityCompletedValue === false) {
    return activityCreateInputs;
  }

  return [];
};

export const getEventId = ({ buttonMetadata, formData, type }) => {
  if (buttonMetadata?.customUpdateState) {
    return buttonMetadata.customUpdateState;
  }

  const activityCompletionCode =
    formData?.isTheActivityCompleted?.additionalData?.data?.code;

  if (type === 'create' && activityCompletionCode === 'YES') {
    return 'completeActivity';
  }

  return formData?.selectState?.value;
};

export const getFilteredAllowedActionsForActivityUpdate = (
  allowedActions: any[] = [],
) => {
  const allowedActionPriority = {
    complete: 0,
    reschedule: 1,
    cancel: 2,
  };

  return allowedActions
    .filter((item) =>
      ['complete', 'cancel', 'reschedule'].includes(item?.allowedAction),
    )
    .sort(
      (a, b) =>
        (allowedActionPriority[a?.allowedAction] ?? Number.MAX_SAFE_INTEGER) -
        (allowedActionPriority[b?.allowedAction] ?? Number.MAX_SAFE_INTEGER),
    );
};

export const getBulkActionInputsConfig = (
  formData: Record<string, FormFieldState>,
  actionType: UploadActionType,
  allocationTypes?: Array<Record<string, string>>,
) => {
  const templateId = formData?.allocationType?.additionalData?.data?.templateId;
  const isBulkRevoke = actionType === BULK_REVOKE;
  const actionEndpoint = isBulkRevoke ? 'bulk-revocation' : 'bulk-allocation';
  const actionCode = isBulkRevoke ? 'bulkRevoke' : 'bulkReassign';
  const actionLabel = isBulkRevoke
    ? locale(Keys.BULK_REVOKE)
    : locale(Keys.BULK_REASSIGN);

  return [
    {
      code: 'allocationType',
      hint: locale(Keys.ALLOCATION_TYPE),
      placeholder: 'Select value',
      returnCode: true,
      required: true,
      type: 'code_name_spinner',
      codeNameSpinnerOptions: allocationTypes,
    },
    {
      multimedia_options: {
        media_type: 'document',
        media_types: ['document', 'photo'],
        mime_types: [
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'text/csv',
        ],
        access: ['local_storage', 'camera', 'gallery'],
        max_files: 1,
        max_size: 20 * 1024 * 1024,
      },
      templateDownloadConfig: {
        label: locale(Keys.DOWNLOAD_TEMPLATE),
        url: `/vymo/v3/workflow/${actionEndpoint}/${templateId}/template`,
        disabled: !templateId,
      },
      code: actionCode,
      hint: actionLabel,
      type: 'multimedia',
    },
  ];
};

export const getInputsConfig = (
  inputsConfig: InputFieldConfig[],
  actionType: string,
  formData: Record<string, FormFieldState>,
  allocationTypes?: Array<Record<string, string>>,
) => {
  if (inputsConfig?.length) {
    return inputsConfig;
  }

  switch (actionType) {
    case BULK_REVOKE:
    case BULK_UPLOAD:
      return getBulkActionInputsConfig(formData, actionType, allocationTypes);

    default:
      return [];
  }
};
